import { Action, createReducer, on } from '@ngrx/store';
import { ListUserDashboardDto } from 'src/app/services/api.services';
import { DashboardsActions } from '.';

export enum DashboardsCategory {
  User = 'user',
  Shared = 'shared',
  Library = 'library'
}

export enum DashboardsDisplayMode {
  Grid = 'grid',
  List = 'list'
}

export interface DashboardsSortMode {
  column: string;
  descending: boolean;
}

export interface DashboardsDisplayParams {
  category?: DashboardsCategory;
  favouriteOnly?: boolean;
  displayMode?: DashboardsDisplayMode;
  sortMode?: DashboardsSortMode;
}

export const DashboardsDefaultDisplayParams: DashboardsDisplayParams = {
  category: DashboardsCategory.User,
  favouriteOnly: false,
  displayMode: DashboardsDisplayMode.Grid,
  sortMode: {
    column: 'updated',
    descending: true
  }
};

export const DashboardsSortColumns = [
  'name', /* description */, 'updatedBy', 'updated', 'created', 'shared'
];

export type DashboardsDashboard = ListUserDashboardDto & {
  onWelcomeScreen?: boolean;
};

export interface State {
  loadingDashboards: boolean;
  copyingDashboard: boolean;
  deletingDashboard: boolean;
  togglingDashboardLibrary: boolean;
  sharingDashboard: boolean;

  dashboards: DashboardsDashboard[];

  selectedDashboardId: number | null;

  displayParams: DashboardsDisplayParams;
}

export const initialState: State = {
  loadingDashboards: false,
  copyingDashboard: false,
  deletingDashboard: false,
  sharingDashboard: false,
  togglingDashboardLibrary: false,

  dashboards: [],

  selectedDashboardId: null,

  displayParams: { ...DashboardsDefaultDisplayParams }
};

const dashboardsReducer = createReducer(
  initialState,

  on(DashboardsActions.getDashboards, (state) => {
    return {
      ...state,
      loadingDashboards: true
    };
  }),
  on(DashboardsActions.getDashboardsSuccess, (state, { dashboards }) => {

    let selectedDashboardId = state.selectedDashboardId;
    if (!dashboards.map(x => x.id).includes(selectedDashboardId))
      selectedDashboardId = null;
    return {
      ...state,
      selectedDashboardId,
      dashboards: dashboards,
      loadingDashboards: false
    };
  }),
  on(DashboardsActions.getDashboardsFailure, (state, { error }) => {
    return {
      ...state,
      loadingDashboards: false,
    };
  }),


  on(DashboardsActions.copyDashboard, (state, { dashboardId }) => {
    return {
      ...state,
      copyingDashboard: true
    };
  }),
  on(DashboardsActions.copyDashboardSuccess, (state, { dashboard }) => {
    return {
      ...state,
      dashboards: [...state.dashboards, dashboard],
      copyingDashboard: false
    };
  }),
  on(DashboardsActions.copyDashboardFailure, (state, { error }) => {
    return {
      ...state,
      copyingDashboard: false,
    };
  }),


  on(DashboardsActions.deleteDashboard, (state, { dashboardId }) => {
    return {
      ...state,
      deletingDashboard: true
    };
  }),
  on(DashboardsActions.deleteDashboardSuccess, (state, { dashboardId }) => {
    let selectedDashboardId = state.selectedDashboardId;

    if (dashboardId === selectedDashboardId)
      selectedDashboardId = null;

    return {
      ...state,
      dashboards: state.dashboards.filter(d => d.id !== dashboardId),
      selectedDashboardId,
      deletingDashboard: false
    };
  }),
  on(DashboardsActions.deleteDashboardFailure, (state, { error }) => {
    return {
      ...state,
      deletingDashboard: false,
    };
  }),


  on(DashboardsActions.shareDashboard, (state, { dashboardId }) => {
    return {
      ...state,
      sharingDashboard: true
    };
  }),
  on(DashboardsActions.shareDashboardSuccess, (state) => {
    return {
      ...state,
      sharingDashboard: false
    };
  }),
  on(DashboardsActions.shareDashboardFailure, (state, { error }) => {
    return {
      ...state,
      sharingDashboard: false,
    };
  }),


  on(DashboardsActions.updateUserDashboard, (state, { dashboard }) => {
    return {
      ...state
    };
  }),
  on(DashboardsActions.updateUserDashboardSuccess, (state, { userDashboard }) => {
    const dashboardIndex = state.dashboards.findIndex(d => d.id === userDashboard.dashboardId);

    if (dashboardIndex === -1)
      return state;

    const dashboards = [...state.dashboards];

    const dashboard = dashboards[dashboardIndex];

    dashboards[dashboardIndex] = {
      ...dashboard,
      favourite: userDashboard.favourite,
      home: userDashboard.home
    };

    return {
      ...state,
      dashboards
    };
  }),
  on(DashboardsActions.updateUserDashboardFailure, (state, { error }) => {
    return {
      ...state,
    };
  }),

  on(DashboardsActions.toggleDashboardLibrary, (state, { dashboard }) => {
    return {
      ...state,
      togglingDashboardLibrary: true
    };
  }),
  on(DashboardsActions.toggleDashboardLibrarySuccess, (state, { dashboardId, fromLibrary }) => {
    const dashboards = [...state.dashboards];

    const dashboardIndex = dashboards.findIndex(d => d.id === dashboardId);

    dashboards[dashboardIndex] = {
      ...dashboards[dashboardIndex],
      fromLibrary
    };

    return {
      ...state,
      dashboards,
      togglingDashboardLibrary: false
    };
  }),
  on(DashboardsActions.toggleDashboardLibraryFailure, (state, { error }) => {
    return {
      ...state,
      togglingDashboardLibrary: false,
    };
  }),


  on(DashboardsActions.setSelectedDashboardId, (state, { dashboardId }) => {
    return {
      ...state,
      selectedDashboardId: dashboardId
    };
  }),

  on(DashboardsActions.updateDisplayParams, (state, { displayParams }) => {
    return {
      ...state,
      displayParams: {
        ...state.displayParams,
        ...displayParams
      }
    };
  }),


  on(DashboardsActions.toggleDashboardOnWelcomeScreen, (state, { dashboard }) => {
    return state;
  }),
  on(DashboardsActions.toggleDashboardOnWelcomeScreenSuccess, (state, { dashboardId, onWelcomeScreen }) => {
    const dashboards = [...state.dashboards];

    const dashboardIndex = dashboards.findIndex(d => d.id === dashboardId);

    dashboards[dashboardIndex] = {
      ...dashboards[dashboardIndex],
      onWelcomeScreen
    };

    return {
      ...state,
      dashboards,
    };
  }),
  on(DashboardsActions.toggleDashboardOnWelcomeScreenFailure, (state, { error }) => {
    return state;
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return dashboardsReducer(state, action);
}
